import { useEffect, useState, useRef } from "react";
import { Navigate, Outlet, Link } from "react-router-dom";
import { LogOut, ChevronLeft, MoreVertical, Users } from "lucide-react";
import useAuthStore from "../store/auth.store";
import { isPlayerAccount } from "../components/common/RequireNotPlayer";
import useWalletStore from "../store/wallet.store";
import WalletBadge from "../components/wallet/WalletBadge";
import WalletModalsHost from "../components/wallet/WalletModalsHost";
import FeedbackWidget from "../components/feedback/FeedbackWidget";
import ThemeToggle from "../components/common/ThemeToggle";
import ErrorBoundary from "../components/common/ErrorBoundary";
import { ToastHost } from "../components/common/ToastHost";
import KeyboardHelp from "../components/common/KeyboardHelp";
import VersionWatcher from "../components/common/VersionWatcher";
import { Loader2 } from "lucide-react";

// Minimal layout for the ball-by-ball scoring console — no full nav so the
// scoring screen keeps maximum vertical space.
//
// Top-right control strip (wallet / theme / matches / exit) is hidden by
// default behind a single launcher button so it never overlays the
// ScoreRibbon. Hover the launcher OR click it to expand; click outside to
// collapse. (Click toggles persist for touchscreens which don't fire hover.)
export default function BallLayout() {
  const { isAuthenticated, loading, user } = useAuthStore();
  const fetchBalance = useWalletStore((s) => s.fetchBalance);
  const initWalletEventListeners = useWalletStore((s) => s.initWalletEventListeners);
  // 2026-06-05 — also subscribe to "wallet_changed" so any in-console
  // AI debit (commentary regen, scorer chat, AI tag, AI suggest) refreshes
  // the WalletBadge balance. Without this the badge displayed stale
  // numbers and operators reported "ZC not getting deducted" even when
  // the backend had correctly debited.
  useEffect(() => {
    if (!isAuthenticated) return;
    initWalletEventListeners();
    fetchBalance();
  }, [isAuthenticated, fetchBalance, initWalletEventListeners]);

  // Player-only accounts can't enter /ball/* (no scoring).
  if (isPlayerAccount(user)) return <Navigate to="/me/player" replace />;

  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Click outside to close — only when click-toggled open; hover-driven open
  // closes when the cursor leaves the wrapper.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-main">
        <div className="flex items-center gap-2 text-text-secondary text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Loading…</span>
        </div>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // Pre-launch lockout — STANDBY accounts can't enter scoring either.
  if (user?.status === "STANDBY") return <Navigate to="/maintenance" replace />;

  return (
    <ToastHost>
    <div className="min-h-screen bg-bg-main text-text-primary">
      <div
        ref={wrapRef}
        className="fixed top-1 right-1 z-50"
      >
        {/* Launcher — pinned to the top-most right corner. Click toggles
            a VERTICAL dropdown so the menu doesn't bleed left over the
            ScoreRibbon / other header chrome. */}
        <button
          type="button"
          aria-label="Open scoring controls"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="w-8 h-8 inline-flex items-center justify-center rounded-md bg-bg-primary/90 backdrop-blur border border-border-default text-text-secondary hover:text-text-primary shadow-sm ml-auto"
          title="Wallet / theme / exit"
        >
          <MoreVertical className="w-4 h-4" />
        </button>

        {/* Expanded dropdown — wallet, theme, matches link, exit. Stacked
            vertically and anchored under the launcher button on the
            right edge so it stays within the viewport. */}
        <div
          className={`absolute right-0 mt-1 flex flex-col gap-1 p-2 rounded-md bg-bg-primary/95 backdrop-blur border border-border-default shadow-lg transition-all duration-150 min-w-[180px] max-w-[calc(100vw-8px)] ${
            open
              ? "opacity-100 translate-y-0 pointer-events-auto"
              : "opacity-0 -translate-y-1 pointer-events-none"
          }`}
        >
          <div className="flex items-center justify-between gap-2 px-1 py-1">
            <span className="text-[10px] uppercase tracking-wider text-text-secondary">Wallet</span>
            <WalletBadge />
          </div>
          <div className="flex items-center justify-between gap-2 px-1 py-1">
            <span className="text-[10px] uppercase tracking-wider text-text-secondary">Theme</span>
            <ThemeToggle compact />
          </div>
          <span className="h-px w-full bg-border-default my-1" />
          {/* 2026-07-08 — mid-match Playing XI editor. BallByBallPage
              listens for this event and opens EditPlayingXIModal with
              its current matchId. Rendered here so the entry point is
              available anywhere in the scoring layout. */}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              window.dispatchEvent(new CustomEvent("edit-playing-xi:open"));
            }}
            className="flex items-center gap-2 h-9 px-2 rounded text-xs uppercase tracking-wider hover:bg-bg-secondary/60 text-text-secondary hover:text-text-primary text-left"
            title="Fix a wrong player in the Playing XI"
          >
            <Users className="w-3.5 h-3.5" />
            Edit Playing XI
          </button>
          <Link
            to="/cricket/matches"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 h-9 px-2 rounded text-xs uppercase tracking-wider hover:bg-bg-secondary/60 text-text-secondary hover:text-text-primary"
            title="Exit scoring → match list"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Matches
          </Link>
          <Link
            to="/dashboard"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 h-9 px-2 rounded text-xs uppercase tracking-wider hover:bg-bg-secondary/60 text-text-secondary hover:text-text-primary"
            title="Exit to dashboard"
          >
            <LogOut className="w-3.5 h-3.5" />
            Exit
          </Link>
        </div>
      </div>
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
      <WalletModalsHost />
      <FeedbackWidget position="left" compact />
      <KeyboardHelp />
      <VersionWatcher />
    </div>
    </ToastHost>
  );
}
